import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../design-system/table/table';
import { TransactionView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { recentTransactionsColumnsDefinition } from './recent-transactions-column-definition';

export type TransactionFilter = 'all' | 'money_in' | 'money_out';

type RecentTransactionsTableProps = {
  transactions: TransactionView[];
  onSelectRow?: (id: string) => void;
  className?: string;
  showFilter?: boolean;
  onViewAll?: () => void;
  maxTransactions?: number;
};

const FILTER_OPTIONS: { value: TransactionFilter; label: string }[] = [
  { value: 'all', label: 'Recent' },
  { value: 'money_in', label: 'Money In' },
  { value: 'money_out', label: 'Money Out' },
];

export const RecentTransactionsTable = ({
  transactions,
  onSelectRow,
  className,
  showFilter = true,
  onViewAll,
  maxTransactions = 10,
}: RecentTransactionsTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const currentFilter = (columnFilters.find((f) => f.id === 'transactionType')?.value as TransactionFilter) || 'all';

  const table = useReactTable({
    data: transactions,
    columns: recentTransactionsColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: maxTransactions,
      },
    },
  });

  return (
    <Stack space={3} className={className}>
      {showFilter && (
        <Inline justify="between" align="center" className="w-full">
          <Inline space={3} align="center">
            <h2 className="text-xl font-medium">Recent Transactions</h2>
            {onViewAll && (
              <Button
                variant="link"
                className="text-seapoint-blue-link"
                iconRight={<ArrowRight size={16} />}
                onClick={onViewAll}
              >
                View all
              </Button>
            )}
          </Inline>
          <Inline role="radiogroup" aria-label="Transaction filter" space={3}>
            {FILTER_OPTIONS.map((option) => {
              const selected = currentFilter === option.value;
              return (
                <Button
                  key={option.value}
                  role="radio"
                  aria-checked={selected}
                  type="button"
                  onClick={() => {
                    table.setColumnFilters([{ id: 'transactionType', value: option.value }]);
                  }}
                  variant={'outline'}
                  className={cn(
                    selected
                      ? 'border-seapoint-gray-border bg-white text-black'
                      : 'border-transparent bg-seapoint-gray-medium text-seapoint-gray-text',
                  )}
                >
                  {option.label}
                </Button>
              );
            })}
          </Inline>
        </Inline>
      )}

      <div className="w-full">
        <Table>
          <TableHeader className="text-xs text-seapoint-gray-text">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers
                  .filter((header) => header.column.columnDef.id !== 'transactionType')
                  .map((header) => (
                    <TableHead key={header.id} className={header.column.getIndex() === 0 ? 'pl-5' : undefined}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="text-sm text-black">
            {table.getRowModel().rows.length ? (
              table
                .getRowModel()
                .rows.slice(0, maxTransactions)
                .map((row, index) => {
                  const isEvenRow = index % 2 === 0;
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn('cursor-pointer px-5', !isEvenRow ? 'bg-seapoint-gray-background/50' : '', {
                        'hover:bg-seapoint-gray-background/50': !isEvenRow,
                        'hover:bg-transparent': isEvenRow,
                      })}
                      onClick={() => onSelectRow?.(row.original.id)}
                    >
                      {row
                        .getVisibleCells()
                        .filter((cell) => cell.column.columnDef.id !== 'transactionType')
                        .map((cell) => (
                          <TableCell key={cell.id} className={cell.column.getIndex() === 0 ? 'pl-5' : undefined}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                    </TableRow>
                  );
                })
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                  <p className="text-sm text-seapoint-gray-text">
                    {currentFilter === 'all'
                      ? 'No transactions found'
                      : currentFilter === 'money_in'
                        ? 'No incoming transactions found'
                        : 'No outgoing transactions found'}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Stack>
  );
};
